import { useEffect } from "react";

/**
 * SEO hook that reliably updates meta tags in the document head.
 * Works with SPAs where react-helmet may not replace existing tags.
 */
export function useSEO({ title, description, ogTitle, ogDescription, ogImage, ogType, canonical, ogUrl, articleMeta, schemaData, language, hreflangLinks }) {
  useEffect(() => {
    // Update title
    if (title) document.title = title;

    // Update html lang attribute
    if (language) {
      document.documentElement.setAttribute("lang", language);
    }

    const updates = [];
    if (description) updates.push({ selector: 'meta[name="description"]', attr: "content", value: description });
    if (ogTitle) updates.push({ selector: 'meta[property="og:title"]', attr: "content", value: ogTitle });
    if (ogDescription) updates.push({ selector: 'meta[property="og:description"]', attr: "content", value: ogDescription });
    if (ogImage) updates.push({ selector: 'meta[property="og:image"]', attr: "content", value: ogImage });
    if (ogType) updates.push({ selector: 'meta[property="og:type"]', attr: "content", value: ogType });
    if (ogUrl) updates.push({ selector: 'meta[property="og:url"]', attr: "content", value: ogUrl });

    // Twitter
    if (ogTitle) updates.push({ selector: 'meta[name="twitter:title"]', attr: "content", value: ogTitle });
    if (ogDescription) updates.push({ selector: 'meta[name="twitter:description"]', attr: "content", value: ogDescription });
    if (ogImage) updates.push({ selector: 'meta[name="twitter:image"]', attr: "content", value: ogImage });

    const originals = {};
    updates.forEach(({ selector, attr, value }) => {
      const el = document.querySelector(selector);
      if (el) {
        originals[selector] = el.getAttribute(attr);
        el.setAttribute(attr, value);
      }
    });

    // Canonical
    let origCanonical;
    if (canonical) {
      const link = document.querySelector('link[rel="canonical"]');
      if (link) {
        origCanonical = link.getAttribute("href");
        link.setAttribute("href", canonical);
      }
    }

    // Article-specific meta tags
    const addedArticleTags = [];
    if (articleMeta) {
      if (articleMeta.publishedTime) {
        const tag = createMeta("article:published_time", articleMeta.publishedTime);
        addedArticleTags.push(tag);
      }
      if (articleMeta.modifiedTime) {
        const tag = createMeta("article:modified_time", articleMeta.modifiedTime);
        addedArticleTags.push(tag);
      }
      if (articleMeta.author) {
        const tag = createMeta("article:author", articleMeta.author);
        addedArticleTags.push(tag);
      }
      if (articleMeta.tags) {
        articleMeta.tags.forEach(t => {
          const tag = createMeta("article:tag", t);
          addedArticleTags.push(tag);
        });
      }
    }

    // Schema.org JSON-LD
    let schemaScript;
    if (schemaData) {
      schemaScript = document.createElement("script");
      schemaScript.type = "application/ld+json";
      schemaScript.setAttribute("data-blog-seo", "true");
      schemaScript.textContent = JSON.stringify(schemaData);
      document.head.appendChild(schemaScript);
    }

    // Hreflang alternate links
    const addedHreflangLinks = [];
    if (hreflangLinks && hreflangLinks.length > 0) {
      hreflangLinks.forEach(({ lang, url }) => {
        const link = document.createElement("link");
        link.setAttribute("rel", "alternate");
        link.setAttribute("hreflang", lang);
        link.setAttribute("href", url);
        link.setAttribute("data-blog-seo", "true");
        document.head.appendChild(link);
        addedHreflangLinks.push(link);
      });
    }

    // Cleanup on unmount
    return () => {
      if (title) document.title = "Uneksia - Unien Tulkinta & Unianalyysi Tekoälyllä";
      if (language) document.documentElement.setAttribute("lang", "fi");
      updates.forEach(({ selector, attr }) => {
        const el = document.querySelector(selector);
        if (el && originals[selector]) el.setAttribute(attr, originals[selector]);
      });
      if (origCanonical) {
        const link = document.querySelector('link[rel="canonical"]');
        if (link) link.setAttribute("href", origCanonical);
      }
      addedArticleTags.forEach(tag => tag.remove());
      addedHreflangLinks.forEach(link => link.remove());
      if (schemaScript) schemaScript.remove();
    };
  }, [title, description, ogTitle, ogDescription, ogImage, ogType, canonical, ogUrl]);
}

function createMeta(property, content) {
  const tag = document.createElement("meta");
  tag.setAttribute("property", property);
  tag.setAttribute("content", content);
  tag.setAttribute("data-blog-seo", "true");
  document.head.appendChild(tag);
  return tag;
}
